import * as React from 'react';
import { cn } from './cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: Variant;
	size?: Size;
	loading?: boolean;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	fullWidth?: boolean;
}

const variants: Record<Variant, string> = {
	primary:
		'bg-brand-800 text-white hover:bg-brand-700 active:bg-brand-900 disabled:bg-brand-300 disabled:text-white shadow-sm',
	secondary:
		'bg-white text-brand-800 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 disabled:text-slate-400 disabled:bg-slate-50',
	ghost:
		'bg-transparent text-brand-800 hover:bg-slate-100 active:bg-slate-200 disabled:text-slate-400',
	danger:
		'bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-800 disabled:bg-danger-200 shadow-sm',
	success:
		'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800 disabled:bg-accent-300 shadow-sm',
};

const sizes: Record<Size, string> = {
	sm: 'h-8 px-3 text-sm rounded-md gap-1.5',
	md: 'h-10 px-4 text-sm rounded-lg gap-2',
	lg: 'h-12 px-6 text-base rounded-lg gap-2',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'primary',
			size = 'md',
			loading,
			leftIcon,
			rightIcon,
			fullWidth,
			className,
			children,
			disabled,
			type = 'button',
			...rest
		},
		ref
	) => (
		<button
			ref={ref}
			type={type}
			disabled={disabled || loading}
			className={cn(
				'inline-flex items-center justify-center font-medium transition-colors',
				'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500',
				'disabled:cursor-not-allowed select-none',
				fullWidth && 'w-full',
				variants[variant],
				sizes[size],
				className
			)}
			{...rest}
		>
			{loading ? (
				<svg
					className="h-4 w-4 animate-spin"
					viewBox="0 0 24 24"
					fill="none"
					aria-hidden
				>
					<circle
						className="opacity-25"
						cx="12"
						cy="12"
						r="10"
						stroke="currentColor"
						strokeWidth="4"
					/>
					<path
						className="opacity-75"
						fill="currentColor"
						d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
					/>
				</svg>
			) : (
				leftIcon
			)}
			{children}
			{!loading && rightIcon}
		</button>
	)
);
Button.displayName = 'Button';

export default Button;
